// Carry Roll theme sync — maps the persisted theme preference to the
// documentElement `dark` class used by Tailwind and the design tokens.

import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../store';
import type { Theme } from '../store/settingsSlice';

const STORAGE_KEY = 'carry-roll.store';
const META_THEME_DARK = '#10140e';
const META_THEME_LIGHT = '#f5f3ec';

export function prefersDark() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function resolveTheme(theme: Theme): 'dark' | 'light' {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light';
  return theme;
}

/** Apply the resolved theme to the document without React (used by the
 *  inline pre-hydration script in index.html). */
export function applyThemeDocument(theme: Theme) {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? META_THEME_DARK : META_THEME_LIGHT);
  }
}

/** Read the persisted theme value directly from localStorage. Returns null
 *  when unavailable so callers can fall back to 'system'. */
export function getStoredTheme(): Theme | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { theme?: Theme } } | null;
    const theme = parsed?.state?.theme;
    if (theme === 'light' || theme === 'dark' || theme === 'system') return theme;
  } catch {
    /* ignore corrupt storage */
  }
  return null;
}

function usePersistHydrated() {
  const [hydrated, setHydrated] = useState(() => useStore.persist.hasHydrated());
  useEffect(() => {
    const unsubHydrate = useStore.persist.onHydrate(() => setHydrated(false));
    const unsubFinish = useStore.persist.onFinishHydration(() => setHydrated(true));
    return () => {
      unsubHydrate();
      unsubFinish();
    };
  }, []);
  return hydrated;
}

export function useTheme() {
  const storeTheme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const hydrated = usePersistHydrated();

  // Until Zustand's persist middleware rehydrates, read the saved theme from
  // localStorage directly so the initial render matches the pre-hydration script
  // in index.html and avoids a preference flash.
  const storedTheme = useMemo(() => getStoredTheme() ?? 'system', []);
  const theme = hydrated ? storeTheme : storedTheme;

  const resolved = useMemo(() => resolveTheme(theme), [theme]);

  useEffect(() => {
    applyThemeDocument(theme);
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    if (storeTheme !== 'system') return;

    const handler = () => applyThemeDocument('system');
    // addEventListener + removeEventListener are available on modern MediaQueryList.
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, storeTheme]);

  return { theme, resolved, setTheme };
}
